import type { AppNotification, ChatMessage, ChatReaction, Gift, GameMode, TogetherMediaType } from "./types";

// Receive-only from this client's perspective: every mutation (send/
// delete/mark-read) goes through the normal authenticated+CSRF-protected
// REST calls in chat.ts. This socket exists purely so the server can push
// what it already persisted - see server.js's WebSocketServer setup for
// the other half of this.

let socket: WebSocket | null = null;
let shouldReconnect = false;
let reconnectDelayMs = 1000;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

let onNewMessage: ((message: ChatMessage) => void) | null = null;
let onMessagesRead: ((conversationId: string, messageIds: string[], readAt: string) => void) | null = null;
let onMessageDeleted: ((conversationId: string, messageId: string, deletedAt: string) => void) | null = null;
let onMessageReaction:
  | ((conversationId: string, messageId: string, reactions: ChatReaction[]) => void)
  | null = null;
let onTypingStarted: ((conversationId: string, fromUserId: string) => void) | null = null;
let onTypingStopped: ((conversationId: string, fromUserId: string) => void) | null = null;
let onMessageEdited: ((conversationId: string, message: ChatMessage) => void) | null = null;

export function setChatSocketHandlers(handlers: {
  onNewMessage: (message: ChatMessage) => void;
  onMessagesRead: (conversationId: string, messageIds: string[], readAt: string) => void;
  onMessageDeleted: (conversationId: string, messageId: string, deletedAt: string) => void;
  onMessageReaction: (conversationId: string, messageId: string, reactions: ChatReaction[]) => void;
  onTypingStarted: (conversationId: string, fromUserId: string) => void;
  onTypingStopped: (conversationId: string, fromUserId: string) => void;
  onMessageEdited: (conversationId: string, message: ChatMessage) => void;
}): void {
  onNewMessage = handlers.onNewMessage;
  onMessagesRead = handlers.onMessagesRead;
  onMessageDeleted = handlers.onMessageDeleted;
  onMessageReaction = handlers.onMessageReaction;
  onTypingStarted = handlers.onTypingStarted;
  onTypingStopped = handlers.onTypingStopped;
  onMessageEdited = handlers.onMessageEdited;
}

// setChatSocketHandlers is exclusive (ChatScreen.tsx's registration, one
// active set at a time) - TogetherChatPanel.tsx needs to ALSO observe
// new_message for its own conversation without stealing that slot, so
// new_message additionally fans out to any number of these, Set-based
// like stateBus.ts's onStateChange, alongside (not instead of) the
// primary handler above.
const extraNewMessageListeners = new Set<(message: ChatMessage) => void>();
export function onAnyNewMessage(fn: (message: ChatMessage) => void): () => void {
  extraNewMessageListeners.add(fn);
  return () => extraNewMessageListeners.delete(fn);
}

// Fired every time the socket (re)connects, including the very first
// connect - Together Mode uses this to resync (togetherSync.ts's
// resyncAfterReconnect) since together_* events are push-only: anything
// that happened while this socket was down (a reconnect can take up to the
// 30s backoff cap) is otherwise silently missed, not just delayed.
const socketOpenListeners = new Set<() => void>();
export function onSocketOpen(fn: () => void): () => void {
  socketOpenListeners.add(fn);
  return () => socketOpenListeners.delete(fn);
}

// Presence (PresenceIndicator.tsx) - same Set-based multi-subscriber shape
// as onAnyNewMessage, since nothing else currently owns this event either.
export interface PresenceChangedData {
  userId: string;
  online: boolean;
  name: string;
}
const presenceListeners = new Set<(data: PresenceChangedData) => void>();
export function onPresenceChanged(fn: (data: PresenceChangedData) => void): () => void {
  presenceListeners.add(fn);
  return () => presenceListeners.delete(fn);
}

// Together Mode rides the exact same connection/reconnect machinery as
// chat - a second, independent handler registry on the same socket,
// dispatched by event name alongside the chat events above, rather than a
// second WebSocket. See server.js's "Together Mode API" section for the
// event payload shapes this mirrors.
export interface TogetherInviteData {
  sessionId: string;
  fromUserId: string;
  fromName: string;
  filename: string;
  mediaType: TogetherMediaType;
  conversationId: string;
}
export interface TogetherAcceptedData {
  sessionId: string;
  filename: string;
  mediaType: TogetherMediaType;
}
export interface TogetherDeclinedData {
  sessionId: string;
}
export interface TogetherSyncData {
  sessionId: string;
  filename: string;
  mediaType: TogetherMediaType;
  position: number;
  playing: boolean;
  fromUserId: string;
  // Ordering signal - see togetherSessionResponse's own comment (server.js)
  // and applyRemoteSync's staleness guard (togetherSync.ts).
  updatedAt: string;
}
export interface TogetherReactionData {
  sessionId: string;
  emoji: string;
  fromUserId: string;
}
// Press-and-hold reactions - a separate stream from the one-shot tap event
// above, not an overload of it (see togetherApi.ts). react_hold fires
// repeatedly (throttled) as the sender's hold intensifies; react_hold_end
// closes it out. Neither is stored on the session server-side - fire and
// forget, same as together_reaction.
export interface TogetherReactionHoldData {
  sessionId: string;
  emoji: string;
  fromUserId: string;
  intensity: number;
}
export interface TogetherReactionHoldEndData {
  sessionId: string;
  emoji: string;
  fromUserId: string;
}
export interface TogetherEndedData {
  sessionId: string;
  reason: "left" | "disconnected" | "expired";
}
// Disconnect-grace notifications - distinct from together_ended, which
// only fires once the grace period actually elapses with no reconnect (or
// on an explicit leave). These just tell the OTHER participant's UI to
// show a subtle "Reconnecting to X…"/"Waiting for X…" state, not an error.
export interface TogetherPartnerDisconnectedData {
  sessionId: string;
  userId: string;
}
export interface TogetherPartnerReconnectedData {
  sessionId: string;
  userId: string;
}
export interface TogetherScheduleCreatedData {
  scheduleId: string;
  creatorName: string;
  filename: string;
  startsAt: string;
  conversationId: string;
}
export interface TogetherScheduleUpdatedData {
  scheduleId: string;
  startsAt: string;
  status: "scheduled" | "ready";
}
export interface TogetherScheduleCancelledData {
  scheduleId: string;
}
export interface TogetherScheduleReadyData {
  scheduleId: string;
  peerName: string;
  filename: string;
  startsAt: string;
}
export interface TogetherScheduleStartedData {
  scheduleId: string;
  session: TogetherSessionData;
}
// Matches server.js's togetherSessionResponse() shape exactly - the full
// viewer-relative session, not a partial patch, since the non-initiating
// participant has no existing local togetherSession to merge into (see
// server.js's "Together Schedules API" comment on why this is a dedicated
// event rather than reusing together_accepted).
export interface TogetherSessionData {
  id: string;
  status: "pending" | "active";
  filename: string;
  mediaType: TogetherMediaType;
  position: number;
  playing: boolean;
  updatedAt: string;
  conversationId: string;
  peerUserId: string;
  peerName: string;
  isHost: boolean;
  partnerConnected: boolean;
}

// Pause & Ask - pauses playback via the session's existing filename/
// mediaType/position fields (the receiving handler feeds these straight
// into togetherSync.ts's existing applyRemoteSync, it does not reimplement
// pause/seek), then layers a question/answer state on top. See
// server.js's pauseAndAsk session field for the server-side shape this
// mirrors.
export interface TogetherPauseAskStartedData {
  sessionId: string;
  filename: string;
  mediaType: TogetherMediaType;
  position: number;
  updatedAt: string;
  category: string;
  question: string;
  chatMessageId: string;
  askedByUserId: string;
}
// `answers` is only present once revealed (both sides answered) - the
// server never sends the peer's answer text before that point, so this
// type mirrors PauseAndAskState's discriminated shape (types.ts) rather
// than making `answers` merely optional.
export type TogetherPauseAskAnsweredData =
  | { sessionId: string; revealed: false; answeredUserId: string }
  | { sessionId: string; revealed: true; answers: { mine: string; peer: string } };
export interface TogetherPauseAskEndedData {
  sessionId: string;
  reason: "resumed" | "skipped";
}

let onTogetherInvite: ((data: TogetherInviteData) => void) | null = null;
let onTogetherAccepted: ((data: TogetherAcceptedData) => void) | null = null;
let onTogetherDeclined: ((data: TogetherDeclinedData) => void) | null = null;
let onTogetherSync: ((data: TogetherSyncData) => void) | null = null;
let onTogetherReaction: ((data: TogetherReactionData) => void) | null = null;
let onTogetherReactionHold: ((data: TogetherReactionHoldData) => void) | null = null;
let onTogetherReactionHoldEnd: ((data: TogetherReactionHoldEndData) => void) | null = null;
let onTogetherPauseAskStarted: ((data: TogetherPauseAskStartedData) => void) | null = null;
let onTogetherPauseAskAnswered: ((data: TogetherPauseAskAnsweredData) => void) | null = null;
let onTogetherPauseAskEnded: ((data: TogetherPauseAskEndedData) => void) | null = null;
let onTogetherEnded: ((data: TogetherEndedData) => void) | null = null;
let onTogetherPartnerDisconnected: ((data: TogetherPartnerDisconnectedData) => void) | null = null;
let onTogetherPartnerReconnected: ((data: TogetherPartnerReconnectedData) => void) | null = null;
let onTogetherScheduleCreated: ((data: TogetherScheduleCreatedData) => void) | null = null;
let onTogetherScheduleUpdated: ((data: TogetherScheduleUpdatedData) => void) | null = null;
let onTogetherScheduleCancelled: ((data: TogetherScheduleCancelledData) => void) | null = null;
let onTogetherScheduleReady: ((data: TogetherScheduleReadyData) => void) | null = null;
let onTogetherScheduleStarted: ((data: TogetherScheduleStartedData) => void) | null = null;

export function setTogetherSocketHandlers(handlers: {
  onTogetherInvite: (data: TogetherInviteData) => void;
  onTogetherAccepted: (data: TogetherAcceptedData) => void;
  onTogetherDeclined: (data: TogetherDeclinedData) => void;
  onTogetherSync: (data: TogetherSyncData) => void;
  onTogetherReaction: (data: TogetherReactionData) => void;
  onTogetherReactionHold: (data: TogetherReactionHoldData) => void;
  onTogetherReactionHoldEnd: (data: TogetherReactionHoldEndData) => void;
  onTogetherPauseAskStarted: (data: TogetherPauseAskStartedData) => void;
  onTogetherPauseAskAnswered: (data: TogetherPauseAskAnsweredData) => void;
  onTogetherPauseAskEnded: (data: TogetherPauseAskEndedData) => void;
  onTogetherEnded: (data: TogetherEndedData) => void;
  onTogetherPartnerDisconnected: (data: TogetherPartnerDisconnectedData) => void;
  onTogetherPartnerReconnected: (data: TogetherPartnerReconnectedData) => void;
  onTogetherScheduleCreated: (data: TogetherScheduleCreatedData) => void;
  onTogetherScheduleUpdated: (data: TogetherScheduleUpdatedData) => void;
  onTogetherScheduleCancelled: (data: TogetherScheduleCancelledData) => void;
  onTogetherScheduleReady: (data: TogetherScheduleReadyData) => void;
  onTogetherScheduleStarted: (data: TogetherScheduleStartedData) => void;
}): void {
  onTogetherInvite = handlers.onTogetherInvite;
  onTogetherAccepted = handlers.onTogetherAccepted;
  onTogetherDeclined = handlers.onTogetherDeclined;
  onTogetherSync = handlers.onTogetherSync;
  onTogetherReaction = handlers.onTogetherReaction;
  onTogetherReactionHold = handlers.onTogetherReactionHold;
  onTogetherReactionHoldEnd = handlers.onTogetherReactionHoldEnd;
  onTogetherPauseAskStarted = handlers.onTogetherPauseAskStarted;
  onTogetherPauseAskAnswered = handlers.onTogetherPauseAskAnswered;
  onTogetherPauseAskEnded = handlers.onTogetherPauseAskEnded;
  onTogetherEnded = handlers.onTogetherEnded;
  onTogetherPartnerDisconnected = handlers.onTogetherPartnerDisconnected;
  onTogetherPartnerReconnected = handlers.onTogetherPartnerReconnected;
  onTogetherScheduleCreated = handlers.onTogetherScheduleCreated;
  onTogetherScheduleUpdated = handlers.onTogetherScheduleUpdated;
  onTogetherScheduleCancelled = handlers.onTogetherScheduleCancelled;
  onTogetherScheduleReady = handlers.onTogetherScheduleReady;
  onTogetherScheduleStarted = handlers.onTogetherScheduleStarted;
}

// Game feature rides the exact same connection/reconnect machinery as
// Together Mode - a third, independent handler registry on the same
// socket. See server.js's "Game API" section for the event payload shapes
// this mirrors. game:card_drawn/game:answered also carry a chatMessageId -
// the card announcement and the answer are REAL chat messages (the answer
// replies to the card via the existing chat replyTo mechanism), so they
// show up in the normal chat thread for free; these WS events exist so the
// game overlay itself can render richly without waiting on a chat fetch.
export interface GameInviteData {
  sessionId: string;
  fromUserId: string;
  fromName: string;
  mode: GameMode;
  intensity: number | null;
  rematch: boolean;
  conversationId: string;
}
export interface GameJoinedData {
  sessionId: string;
  mode: GameMode;
  isMyTurn: boolean;
}
export interface GameDeclinedData {
  sessionId: string;
}
export interface GameCardDrawnData {
  sessionId: string;
  cardId: string;
  deckId: string;
  category: string;
  text: string;
  chatMessageId: string;
  progress: number;
}
export interface GameAnsweredData {
  sessionId: string;
  text: string;
  senderName: string;
  chatMessageId: string;
  isMyTurn: boolean;
}
export interface GameSkippedData {
  sessionId: string;
  isMyTurn: boolean;
}
export interface GameFinishedData {
  sessionId: string;
}
export interface GameEndedData {
  sessionId: string;
  reason: "left" | "disconnected" | "expired";
}

let onGameInvite: ((data: GameInviteData) => void) | null = null;
let onGameJoined: ((data: GameJoinedData) => void) | null = null;
let onGameDeclined: ((data: GameDeclinedData) => void) | null = null;
let onGameCardDrawn: ((data: GameCardDrawnData) => void) | null = null;
let onGameAnswered: ((data: GameAnsweredData) => void) | null = null;
let onGameSkipped: ((data: GameSkippedData) => void) | null = null;
let onGameFinished: ((data: GameFinishedData) => void) | null = null;
let onGameEnded: ((data: GameEndedData) => void) | null = null;

export function setGameSocketHandlers(handlers: {
  onGameInvite: (data: GameInviteData) => void;
  onGameJoined: (data: GameJoinedData) => void;
  onGameDeclined: (data: GameDeclinedData) => void;
  onGameCardDrawn: (data: GameCardDrawnData) => void;
  onGameAnswered: (data: GameAnsweredData) => void;
  onGameSkipped: (data: GameSkippedData) => void;
  onGameFinished: (data: GameFinishedData) => void;
  onGameEnded: (data: GameEndedData) => void;
}): void {
  onGameInvite = handlers.onGameInvite;
  onGameJoined = handlers.onGameJoined;
  onGameDeclined = handlers.onGameDeclined;
  onGameCardDrawn = handlers.onGameCardDrawn;
  onGameAnswered = handlers.onGameAnswered;
  onGameSkipped = handlers.onGameSkipped;
  onGameFinished = handlers.onGameFinished;
  onGameEnded = handlers.onGameEnded;
}

// Couple Wheel rides the exact same connection/reconnect machinery as
// Together Mode/Game above - an independent handler registry on the same
// socket. See server.js's "Couple Wheel API" section for the event payload
// shapes this mirrors. Unlike Game's card_drawn/answered, wheel:result
// carries no chatMessageId - spins are session-only, not posted to chat.
export interface WheelInviteData {
  sessionId: string;
  fromUserId: string;
  fromName: string;
  category: string;
  conversationId: string;
}
export interface WheelJoinedData {
  sessionId: string;
  category: string;
}
export interface WheelDeclinedData {
  sessionId: string;
}
export interface WheelResultData {
  sessionId: string;
  resultId: string;
  spinNumber: number;
  fromUserId: string;
}
export interface WheelReactionData {
  sessionId: string;
  emoji: string;
  fromUserId: string;
}
export interface WheelEndedData {
  sessionId: string;
  reason: "left" | "disconnected" | "expired";
}

let onWheelInvite: ((data: WheelInviteData) => void) | null = null;
let onWheelJoined: ((data: WheelJoinedData) => void) | null = null;
let onWheelDeclined: ((data: WheelDeclinedData) => void) | null = null;
let onWheelResult: ((data: WheelResultData) => void) | null = null;
let onWheelReaction: ((data: WheelReactionData) => void) | null = null;
let onWheelEnded: ((data: WheelEndedData) => void) | null = null;

export function setWheelSocketHandlers(handlers: {
  onWheelInvite: (data: WheelInviteData) => void;
  onWheelJoined: (data: WheelJoinedData) => void;
  onWheelDeclined: (data: WheelDeclinedData) => void;
  onWheelResult: (data: WheelResultData) => void;
  onWheelReaction: (data: WheelReactionData) => void;
  onWheelEnded: (data: WheelEndedData) => void;
}): void {
  onWheelInvite = handlers.onWheelInvite;
  onWheelJoined = handlers.onWheelJoined;
  onWheelDeclined = handlers.onWheelDeclined;
  onWheelResult = handlers.onWheelResult;
  onWheelReaction = handlers.onWheelReaction;
  onWheelEnded = handlers.onWheelEnded;
}

// Gifts get three events: gift_received when the sender first sends it,
// gift_unlocked, pushed by the server's unlock sweep when a time-locked
// gift's unlockAt passes (server.js's 30s sweep interval), and
// gift_reaction, pushed to the SENDER when the recipient reacts to an
// opened Private Drop. pushToUser already scopes delivery to the
// recipient's/sender's own sockets, so any frame received here is
// inherently this client's own gift.
export type GiftReceivedData = Gift;
export type GiftUnlockedData = Gift;
export interface GiftReactionData {
  giftId: string;
  reactions: Gift["reactions"];
}
// Private Drops V2
export type GiftEditedData = Gift;
export interface GiftCancelledData {
  giftId: string;
}

let onGiftReceived: ((data: GiftReceivedData) => void) | null = null;
let onGiftUnlocked: ((data: GiftUnlockedData) => void) | null = null;
let onGiftReaction: ((data: GiftReactionData) => void) | null = null;
let onGiftCancelled: ((data: GiftCancelledData) => void) | null = null;
let onGiftEdited: ((data: GiftEditedData) => void) | null = null;

export function setGiftSocketHandlers(handlers: {
  onGiftReceived: (data: GiftReceivedData) => void;
  onGiftUnlocked: (data: GiftUnlockedData) => void;
  onGiftReaction: (data: GiftReactionData) => void;
  onGiftCancelled: (data: GiftCancelledData) => void;
  onGiftEdited: (data: GiftEditedData) => void;
}): void {
  onGiftReceived = handlers.onGiftReceived;
  onGiftUnlocked = handlers.onGiftUnlocked;
  onGiftReaction = handlers.onGiftReaction;
  onGiftCancelled = handlers.onGiftCancelled;
  onGiftEdited = handlers.onGiftEdited;
}

// Open Together's start/waiting events need MULTIPLE simultaneous
// observers (GiftsBody, globally, to call openGiftViewer regardless of
// screen; the waiting-room component itself, to update its own "partner
// joined" indicator while mounted) - same Set-based multi-subscriber shape
// as onPresenceChanged/onAnyNewMessage above, not the exclusive
// setGiftSocketHandlers slot.
export interface OpenTogetherStartData {
  giftId: string;
  startedAt: string;
}
export interface OpenTogetherWaitingData {
  giftId: string;
  partnerJoined: boolean;
}
const openTogetherStartListeners = new Set<(data: OpenTogetherStartData) => void>();
export function onOpenTogetherStart(fn: (data: OpenTogetherStartData) => void): () => void {
  openTogetherStartListeners.add(fn);
  return () => openTogetherStartListeners.delete(fn);
}
const openTogetherWaitingListeners = new Set<(data: OpenTogetherWaitingData) => void>();
export function onOpenTogetherWaiting(fn: (data: OpenTogetherWaitingData) => void): () => void {
  openTogetherWaitingListeners.add(fn);
  return () => openTogetherWaitingListeners.delete(fn);
}

// Party Room (self-hosted shared listening) rides the exact same
// connection/reconnect machinery as Together Mode/Game above - a fourth,
// independent handler registry on the same socket. See server.js's "Party
// Room API" section for the event payload shapes this mirrors.
export interface PartyInviteData {
  sessionId: string;
  fromUserId: string;
  fromName: string;
  conversationId: string;
}
export interface PartyQueueItemData {
  queueId: string;
  filename: string;
  title: string;
  addedByUserId: string;
  addedByName?: string;
}
// Matches server.js's partySessionResponse() shape exactly - the full
// viewer-relative session, same reasoning as TogetherSessionData (the
// accepting side has no existing local partySession to merge a patch into).
export interface PartySessionData {
  id: string;
  status: "pending" | "active";
  queue: PartyQueueItemData[];
  currentIndex: number;
  position: number;
  playing: boolean;
  conversationId: string;
  peerUserId: string;
  peerName: string;
  isHost: boolean;
}
export interface PartyAcceptedData {
  sessionId: string;
  session: PartySessionData;
}
export interface PartyDeclinedData {
  sessionId: string;
}
export interface PartyQueueChangedData {
  sessionId: string;
  queue: PartyQueueItemData[];
  currentIndex: number;
}
export interface PartySyncData {
  sessionId: string;
  currentIndex: number;
  position: number;
  playing: boolean;
  fromUserId: string;
}
export interface PartyReactionData {
  sessionId: string;
  emoji: string;
  fromUserId: string;
}
export interface PartyEndedData {
  sessionId: string;
  reason: "left" | "disconnected" | "expired";
}

let onPartyInvite: ((data: PartyInviteData) => void) | null = null;
let onPartyAccepted: ((data: PartyAcceptedData) => void) | null = null;
let onPartyDeclined: ((data: PartyDeclinedData) => void) | null = null;
let onPartyQueueChanged: ((data: PartyQueueChangedData) => void) | null = null;
let onPartySync: ((data: PartySyncData) => void) | null = null;
let onPartyReaction: ((data: PartyReactionData) => void) | null = null;
let onPartyEnded: ((data: PartyEndedData) => void) | null = null;

export function setPartySocketHandlers(handlers: {
  onPartyInvite: (data: PartyInviteData) => void;
  onPartyAccepted: (data: PartyAcceptedData) => void;
  onPartyDeclined: (data: PartyDeclinedData) => void;
  onPartyQueueChanged: (data: PartyQueueChangedData) => void;
  onPartySync: (data: PartySyncData) => void;
  onPartyReaction: (data: PartyReactionData) => void;
  onPartyEnded: (data: PartyEndedData) => void;
}): void {
  onPartyInvite = handlers.onPartyInvite;
  onPartyAccepted = handlers.onPartyAccepted;
  onPartyDeclined = handlers.onPartyDeclined;
  onPartyQueueChanged = handlers.onPartyQueueChanged;
  onPartySync = handlers.onPartySync;
  onPartyReaction = handlers.onPartyReaction;
  onPartyEnded = handlers.onPartyEnded;
}

// Replaces what used to be a blind 2-minute poll (AuthGate.tsx) for
// anything that didn't already have its own push event: the notifications
// bell, and "something changed in albums/gallery I should refetch". Albums/
// media deliberately carry no payload - see server.js's broadcastToOthers
// comment for why the client just refetches instead of the server trying
// to reconstruct the full item shape in the push itself.
export interface NotificationCreatedData {
  notification: AppNotification;
}
export type AlbumsChangedData = Record<string, never>;
export type MediaChangedData = Record<string, never>;
// Gift Experience V2 - pushed after a virtual gift purchase so a second
// open tab/device of the SAME user (the one who just spent credits) picks
// up the new balance live, without needing to reload (server.js's PART 30
// comment).
export interface CreditsUpdatedData {
  balance: number;
}

let onNotificationCreated: ((data: NotificationCreatedData) => void) | null = null;
let onAlbumsChanged: ((data: AlbumsChangedData) => void) | null = null;
let onMediaChanged: ((data: MediaChangedData) => void) | null = null;
let onCreditsUpdated: ((data: CreditsUpdatedData) => void) | null = null;

export function setSyncSocketHandlers(handlers: {
  onNotificationCreated: (data: NotificationCreatedData) => void;
  onAlbumsChanged: (data: AlbumsChangedData) => void;
  onMediaChanged: (data: MediaChangedData) => void;
  onCreditsUpdated: (data: CreditsUpdatedData) => void;
}): void {
  onNotificationCreated = handlers.onNotificationCreated;
  onAlbumsChanged = handlers.onAlbumsChanged;
  onMediaChanged = handlers.onMediaChanged;
  onCreditsUpdated = handlers.onCreditsUpdated;
}

function clearReconnectTimer(): void {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
}

function scheduleReconnect(): void {
  if (!shouldReconnect || reconnectTimer) return;
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    connectChatSocket();
  }, reconnectDelayMs);
  reconnectDelayMs = Math.min(reconnectDelayMs * 2, 30000);
}

function handleFrame(raw: string): void {
  let parsed: { event?: string; data?: unknown };
  try {
    parsed = JSON.parse(raw);
  } catch {
    return;
  }

  if (parsed.event === "new_message") {
    const data = parsed.data as { message: ChatMessage };
    if (onNewMessage) onNewMessage(data.message);
    extraNewMessageListeners.forEach((fn) => fn(data.message));
  } else if (parsed.event === "presence_changed") {
    const data = parsed.data as PresenceChangedData;
    presenceListeners.forEach((fn) => fn(data));
  } else if (parsed.event === "messages_read" && onMessagesRead) {
    const data = parsed.data as { conversationId: string; messageIds: string[]; readAt: string };
    onMessagesRead(data.conversationId, data.messageIds, data.readAt);
  } else if (parsed.event === "message_deleted" && onMessageDeleted) {
    const data = parsed.data as { conversationId: string; messageId: string; deletedAt: string };
    onMessageDeleted(data.conversationId, data.messageId, data.deletedAt);
  } else if (parsed.event === "message_reaction" && onMessageReaction) {
    const data = parsed.data as { conversationId: string; messageId: string; reactions: ChatReaction[] };
    onMessageReaction(data.conversationId, data.messageId, data.reactions);
  } else if (parsed.event === "typing_started" && onTypingStarted) {
    const data = parsed.data as { conversationId: string; fromUserId: string };
    onTypingStarted(data.conversationId, data.fromUserId);
  } else if (parsed.event === "typing_stopped" && onTypingStopped) {
    const data = parsed.data as { conversationId: string; fromUserId: string };
    onTypingStopped(data.conversationId, data.fromUserId);
  } else if (parsed.event === "message_edited" && onMessageEdited) {
    const data = parsed.data as { conversationId: string; message: ChatMessage };
    onMessageEdited(data.conversationId, data.message);
  } else if (parsed.event === "together_invite" && onTogetherInvite) {
    onTogetherInvite(parsed.data as TogetherInviteData);
  } else if (parsed.event === "together_accepted" && onTogetherAccepted) {
    onTogetherAccepted(parsed.data as TogetherAcceptedData);
  } else if (parsed.event === "together_declined" && onTogetherDeclined) {
    onTogetherDeclined(parsed.data as TogetherDeclinedData);
  } else if (parsed.event === "together_sync" && onTogetherSync) {
    onTogetherSync(parsed.data as TogetherSyncData);
  } else if (parsed.event === "together_reaction" && onTogetherReaction) {
    onTogetherReaction(parsed.data as TogetherReactionData);
  } else if (parsed.event === "together_reaction_hold" && onTogetherReactionHold) {
    onTogetherReactionHold(parsed.data as TogetherReactionHoldData);
  } else if (parsed.event === "together_reaction_hold_end" && onTogetherReactionHoldEnd) {
    onTogetherReactionHoldEnd(parsed.data as TogetherReactionHoldEndData);
  } else if (parsed.event === "together_pause_ask_started" && onTogetherPauseAskStarted) {
    onTogetherPauseAskStarted(parsed.data as TogetherPauseAskStartedData);
  } else if (parsed.event === "together_pause_ask_answered" && onTogetherPauseAskAnswered) {
    onTogetherPauseAskAnswered(parsed.data as TogetherPauseAskAnsweredData);
  } else if (parsed.event === "together_pause_ask_ended" && onTogetherPauseAskEnded) {
    onTogetherPauseAskEnded(parsed.data as TogetherPauseAskEndedData);
  } else if (parsed.event === "together_ended" && onTogetherEnded) {
    onTogetherEnded(parsed.data as TogetherEndedData);
  } else if (parsed.event === "together_partner_disconnected" && onTogetherPartnerDisconnected) {
    onTogetherPartnerDisconnected(parsed.data as TogetherPartnerDisconnectedData);
  } else if (parsed.event === "together_partner_reconnected" && onTogetherPartnerReconnected) {
    onTogetherPartnerReconnected(parsed.data as TogetherPartnerReconnectedData);
  } else if (parsed.event === "together_schedule_created" && onTogetherScheduleCreated) {
    onTogetherScheduleCreated(parsed.data as TogetherScheduleCreatedData);
  } else if (parsed.event === "together_schedule_updated" && onTogetherScheduleUpdated) {
    onTogetherScheduleUpdated(parsed.data as TogetherScheduleUpdatedData);
  } else if (parsed.event === "together_schedule_cancelled" && onTogetherScheduleCancelled) {
    onTogetherScheduleCancelled(parsed.data as TogetherScheduleCancelledData);
  } else if (parsed.event === "together_schedule_ready" && onTogetherScheduleReady) {
    onTogetherScheduleReady(parsed.data as TogetherScheduleReadyData);
  } else if (parsed.event === "together_schedule_started" && onTogetherScheduleStarted) {
    onTogetherScheduleStarted(parsed.data as TogetherScheduleStartedData);
  } else if (parsed.event === "gift_received" && onGiftReceived) {
    onGiftReceived(parsed.data as GiftReceivedData);
  } else if (parsed.event === "gift_unlocked" && onGiftUnlocked) {
    onGiftUnlocked(parsed.data as GiftUnlockedData);
  } else if (parsed.event === "gift_reaction" && onGiftReaction) {
    onGiftReaction(parsed.data as GiftReactionData);
  } else if (parsed.event === "gift_cancelled" && onGiftCancelled) {
    onGiftCancelled(parsed.data as GiftCancelledData);
  } else if (parsed.event === "gift_edited" && onGiftEdited) {
    onGiftEdited(parsed.data as GiftEditedData);
  } else if (parsed.event === "open_together_start") {
    for (const fn of openTogetherStartListeners) fn(parsed.data as OpenTogetherStartData);
  } else if (parsed.event === "open_together_waiting") {
    for (const fn of openTogetherWaitingListeners) fn(parsed.data as OpenTogetherWaitingData);
  } else if (parsed.event === "notification_created" && onNotificationCreated) {
    onNotificationCreated(parsed.data as NotificationCreatedData);
  } else if (parsed.event === "albums_changed" && onAlbumsChanged) {
    onAlbumsChanged(parsed.data as AlbumsChangedData);
  } else if (parsed.event === "media_changed" && onMediaChanged) {
    onMediaChanged(parsed.data as MediaChangedData);
  } else if (parsed.event === "credits_updated" && onCreditsUpdated) {
    onCreditsUpdated(parsed.data as CreditsUpdatedData);
  } else if (parsed.event === "game:invite" && onGameInvite) {
    onGameInvite(parsed.data as GameInviteData);
  } else if (parsed.event === "game:joined" && onGameJoined) {
    onGameJoined(parsed.data as GameJoinedData);
  } else if (parsed.event === "game:declined" && onGameDeclined) {
    onGameDeclined(parsed.data as GameDeclinedData);
  } else if (parsed.event === "game:card_drawn" && onGameCardDrawn) {
    onGameCardDrawn(parsed.data as GameCardDrawnData);
  } else if (parsed.event === "game:answered" && onGameAnswered) {
    onGameAnswered(parsed.data as GameAnsweredData);
  } else if (parsed.event === "game:skipped" && onGameSkipped) {
    onGameSkipped(parsed.data as GameSkippedData);
  } else if (parsed.event === "game:finished" && onGameFinished) {
    onGameFinished(parsed.data as GameFinishedData);
  } else if (parsed.event === "game:ended" && onGameEnded) {
    onGameEnded(parsed.data as GameEndedData);
  } else if (parsed.event === "wheel:invite" && onWheelInvite) {
    onWheelInvite(parsed.data as WheelInviteData);
  } else if (parsed.event === "wheel:joined" && onWheelJoined) {
    onWheelJoined(parsed.data as WheelJoinedData);
  } else if (parsed.event === "wheel:declined" && onWheelDeclined) {
    onWheelDeclined(parsed.data as WheelDeclinedData);
  } else if (parsed.event === "wheel:result" && onWheelResult) {
    onWheelResult(parsed.data as WheelResultData);
  } else if (parsed.event === "wheel:reaction" && onWheelReaction) {
    onWheelReaction(parsed.data as WheelReactionData);
  } else if (parsed.event === "wheel:ended" && onWheelEnded) {
    onWheelEnded(parsed.data as WheelEndedData);
  } else if (parsed.event === "party_invite" && onPartyInvite) {
    onPartyInvite(parsed.data as PartyInviteData);
  } else if (parsed.event === "party_accepted" && onPartyAccepted) {
    onPartyAccepted(parsed.data as PartyAcceptedData);
  } else if (parsed.event === "party_declined" && onPartyDeclined) {
    onPartyDeclined(parsed.data as PartyDeclinedData);
  } else if (parsed.event === "party_queue_changed" && onPartyQueueChanged) {
    onPartyQueueChanged(parsed.data as PartyQueueChangedData);
  } else if (parsed.event === "party_sync" && onPartySync) {
    onPartySync(parsed.data as PartySyncData);
  } else if (parsed.event === "party_reaction" && onPartyReaction) {
    onPartyReaction(parsed.data as PartyReactionData);
  } else if (parsed.event === "party_ended" && onPartyEnded) {
    onPartyEnded(parsed.data as PartyEndedData);
  }
}

// Live connection-state indicator (Watch Together's status bar) - separate
// from onSocketOpen above, which only fires on a successful (re)connect
// and is used for resync logic, not display. This fires on both
// directions so a dropped connection can show something other than
// silently-still-green while scheduleReconnect() backs off.
const connectionChangeListeners = new Set<(connected: boolean) => void>();
export function onSocketConnectionChange(fn: (connected: boolean) => void): () => void {
  connectionChangeListeners.add(fn);
  return () => connectionChangeListeners.delete(fn);
}
export function isSocketConnected(): boolean {
  return !!socket && socket.readyState === WebSocket.OPEN;
}

// Long-idle audit finding: the WS upgrade handler (server.js) rejects with
// a raw HTTP 401 when the session cookie is missing/expired, but the
// browser's WebSocket API gives onclose/onerror no way to see that status
// code - so a genuinely-expired session previously made this reconnect
// forever, silently, every <=30s (the backoff cap), with nothing ever
// telling the auth layer anything was wrong. consecutiveFailures tracks
// closes-without-a-successful-open; once it crosses REPEATED_FAILURE_THRESHOLD,
// onRepeatedFailure fires so the caller (AuthGate.tsx) can check whether
// the SESSION itself is still valid before continuing to retry the socket.
// If it's still valid, this is just a transient server-side hiccup and
// reconnecting keeps going as normal; if not, AuthGate's lock flow calls
// disconnectChatSocket() below, which correctly halts the loop.
const REPEATED_FAILURE_THRESHOLD = 3;
let consecutiveFailures = 0;
let onRepeatedFailure: (() => void) | null = null;
export function setOnRepeatedFailure(fn: (() => void) | null): void {
  onRepeatedFailure = fn;
}

export function connectChatSocket(): void {
  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) return;

  shouldReconnect = true;
  clearReconnectTimer();

  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  socket = new WebSocket(`${proto}//${location.host}/ws/chat`);

  socket.onopen = () => {
    reconnectDelayMs = 1000;
    consecutiveFailures = 0;
    socketOpenListeners.forEach((fn) => fn());
    connectionChangeListeners.forEach((fn) => fn(true));
  };
  socket.onmessage = (e) => handleFrame(e.data);
  socket.onclose = () => {
    socket = null;
    connectionChangeListeners.forEach((fn) => fn(false));
    consecutiveFailures += 1;
    if (consecutiveFailures === REPEATED_FAILURE_THRESHOLD) onRepeatedFailure?.();
    scheduleReconnect();
  };
  socket.onerror = () => {
    // onclose fires right after in browsers - reconnect is handled there.
  };
}

export function disconnectChatSocket(): void {
  shouldReconnect = false;
  clearReconnectTimer();
  reconnectDelayMs = 1000;
  socket?.close();
  socket = null;
}

// Mobile PWAs commonly suspend/drop sockets when backgrounded - reconnect
// promptly instead of waiting for the next backoff tick once the app is
// actually usable again.
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible" && shouldReconnect) connectChatSocket();
});
window.addEventListener("online", () => {
  if (shouldReconnect) connectChatSocket();
});
